import { useEffect } from "react";

const SITE_URL = "https://wagenhofer.capital";

/**
 * Lightweight per-route SEO manager. Updates <title>, meta description,
 * canonical, Open Graph, Twitter tags and optional JSON-LD on mount.
 * Restores previous values on unmount so navigating between pages stays clean.
 */
export default function SEO({
  title,
  description,
  path = "/",
  image,
  type = "website",
  jsonLd,
}) {
  useEffect(() => {
    const canonical = `${SITE_URL}${path}`;
    const ogImage =
      image || "https://wagenhofer.capital/og-image.jpg";

    const previousTitle = document.title;
    if (title) document.title = title;

    const setMeta = (selector, attr, attrVal, content) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
      return el;
    };

    setMeta(
      'meta[name="description"]',
      "name",
      "description",
      description || ""
    );
    setMeta('meta[property="og:title"]', "property", "og:title", title || "");
    setMeta(
      'meta[property="og:description"]',
      "property",
      "og:description",
      description || ""
    );
    setMeta('meta[property="og:url"]', "property", "og:url", canonical);
    setMeta('meta[property="og:type"]', "property", "og:type", type);
    setMeta('meta[property="og:image"]', "property", "og:image", ogImage);
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", title || "");
    setMeta(
      'meta[name="twitter:description"]',
      "name",
      "twitter:description",
      description || ""
    );
    setMeta('meta[name="twitter:image"]', "name", "twitter:image", ogImage);

    // Canonical
    let canonicalEl = document.head.querySelector('link[rel="canonical"]');
    if (!canonicalEl) {
      canonicalEl = document.createElement("link");
      canonicalEl.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalEl);
    }
    const previousCanonical = canonicalEl.getAttribute("href");
    canonicalEl.setAttribute("href", canonical);

    // Optional JSON-LD blob, tagged so we can remove on unmount
    let ldScript = null;
    if (jsonLd) {
      ldScript = document.createElement("script");
      ldScript.type = "application/ld+json";
      ldScript.setAttribute("data-seo-jsonld", "true");
      ldScript.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(ldScript);
    }

    return () => {
      document.title = previousTitle;
      if (previousCanonical && canonicalEl) {
        canonicalEl.setAttribute("href", previousCanonical);
      }
      if (ldScript && ldScript.parentNode) {
        ldScript.parentNode.removeChild(ldScript);
      }
    };
  }, [title, description, path, image, type, jsonLd]);

  return null;
}
